import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { getFormSyncErrors, getFormValues, isDirty, destroy, change } from "redux-form";
import DynamicForm from "../DynamicForm";
import Loader from "../Loader";
import permissions, { checkPermissions } from "config/permissions";
import cx from "classnames";
import { isEmpty } from "lodash";
import ReactSVG from "react-svg";

import { getDynamicFormSturcture } from "./helpers";
import { configureFilterObject } from "../util/configureFilterObject";

import {
    closeFilterForm,
    setAttributes,
    openFilterParams,
    setFilters,
    setFiltersLoading,
    setFiltersIsOpen,
} from "reducers/attributesFilters";

import "./styles.scss";
import BaseService from "../../../../services/BaseService";

const FORM_HEADER_NAME = (storeKey, extraFormKey) =>
    extraFormKey ? `${storeKey}-${extraFormKey}-filters-header` : `${storeKey}-filters-header`;
const FORM_MAIN_NAME = (storeKey, extraFormKey) =>
    extraFormKey ? `${storeKey}-${extraFormKey}-filters-main` : `${storeKey}-filters-main`;

const mapStateToProps = (state, ownProps) => {
    const attributesFilters = state.attributesFilters[ownProps.storeKey];

    return {
        attributes: attributesFilters && attributesFilters.attributes,
        filters: attributesFilters && attributesFilters.filters,
        isFiltersLoading: attributesFilters && attributesFilters.isFiltersLoading,
        isOpen:
            attributesFilters && ![null, undefined].includes(attributesFilters.isOpen)
                ? attributesFilters.isOpen
                : true,
        isCanCreatePublicFilter: checkPermissions(permissions.FilterOperations.create_public_filter),
        formHeaderValues: getFormValues(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formHeaderErrors: getFormSyncErrors(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formMainValues: getFormValues(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formMainErrors: getFormSyncErrors(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        isFormHeaderDirty: isDirty(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        isFormMainDirty: isDirty(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
    };
};

const mapDispatchToProps = {
    closeFilterForm,
    setAttributes,
    openFilterParams,
    setFilters,
    setFiltersLoading,
    setFiltersIsOpen,
    destroy,
    change,
};

const SidebarFiltersForm = props => {
    const {
        t,
        isCanCreatePublicFilter,
        storeKey,
        closeFilterForm,
        activeFilter,
        isNewFilter,
        isEditFilter,
        isFilterParams,
        filters,
        attributes,
        setAttributes,
        attributesRequestKey,
        attributesRequestParams = {},
        attributesValuesRequestKey,
        attributesValuesRequestParams = {},
        formHeaderValues,
        formHeaderErrors,
        formMainValues,
        formMainErrors,
        onFilterSave,
        openFilterParams,
        isFiltersLoading,
        isFormHeaderDirty,
        isFormMainDirty,
        withLocalFiltersList,
        withHeaderForm = true,
        withoutSave,
        withoutClearButton,
        gridWithFilters,
        setFilters,
        setFiltersLoading,
        setFiltersIsOpen,
        filtersCode,
        isKeepOnUnmount,
        extraFormKey,
        destroy,
        change,
        additionalWidgets,
        initialOpenState,
        isOpen,
        withToggle,
        onToggle
    } = props;

    if (!storeKey) {
        console.error("SidebarFiltersForm:: No {storeKey} is provided!");
    }

    // const [attributes, setAttributes] = useState(null);
    const [attributesValues, setAttributesValues] = useState([]);
    const [dynParams, setDynParams] = useState(null);
    const [isAttributesLoading, setIsAttributesLoading] = useState(false);
    const [isValuesLoading, setIsValuesLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const [forceHeaderInitialValues, setForceHeaderInitialValues] = useState(null);
    const [forceMainInitialValues, setForceMainInitialValues] = useState(null);
    const [isFormValuesUpdateRequired, setIsFormValuesUpdateRequired] = useState(false);

    const [headerFormTimestamp, setHeaderFormTimestamp] = useState(null); // used to remount header form in case there are no withLocalFiltersList prop
    const [dynParamsTimestamp, setDynParamsTimestamp] = useState(null);

    const isLoading = isAttributesLoading || isValuesLoading || isSaving;

    const activeFilterId = useMemo(() => activeFilter && activeFilter.filterId, [activeFilter]);

    const isNoErrors = isEmpty(formHeaderErrors) && isEmpty(formMainErrors);

    const isFormDirty = isEditFilter ? isFormHeaderDirty || isFormMainDirty : isFormMainDirty;

    const formMainValuesWithoutHidden = useMemo(() => {
        let values = {};
        if (dynParams && Array.isArray(dynParams) && !isEmpty(formMainValues)) {
            const widgets = dynParams.flatMap(block => block.widgets);
            const hiddenWidgetsKey = widgets.filter(widget => widget.isHidden).map(widget => widget.key);
            if (hiddenWidgetsKey && hiddenWidgetsKey.length > 0) {
                Object.keys(formMainValues).filter(key => !hiddenWidgetsKey.includes(key)).forEach(key => {
                    values[key] = formMainValues[key];
                });
                return values;
            }
        }
        return formMainValues;
    }, [dynParams, formMainValues]);

    const useLocalFilter = useMemo(() => {
        return formHeaderValues && formHeaderValues.useFilter;
    }, [formHeaderValues]);

    const formRerenderKey = withLocalFiltersList
        ? useLocalFilter
        : activeFilter &&
          `${activeFilter && activeFilter.filterId}_${activeFilter &&
              activeFilter.filterName}_${isNewFilter}_${isFilterParams}_${isEditFilter}`;

    const initHeaderForm = useMemo(() => {
        if (withLocalFiltersList) {
            const values = !filters ? [] : filters.map(filter => ({ value: filter.filterName, key: filter.filterId }));
            return [
                {
                    view: "1column",
                    collapsed: false,
                    widgets: [
                        {
                            key: "useFilter",
                            // title: t("filters.useFilter"),
                            values,
                            widgetType: "select",
                            svgIconPath: "/data/svg/filters_list_06.svg",
                        },
                    ],
                },
            ];
        }
        const widgets = [
            {
                key: "filterName",
                title: t("filterName"),
                clearable: true,
                isReq: true,
                maxLength: 200,
                disabled: isFilterParams,
                savedValue: isNewFilter ? `Фiльтр ${filters.length + 2}` : activeFilter && activeFilter.filterName,
                widgetType: "input",
            },
        ];
        if (isCanCreatePublicFilter) {
            widgets.push({
                key: "isPublic",
                title: t("filterIsPublic"),
                clearable: true,
                savedValue: isNewFilter ? false : activeFilter && (activeFilter.predefined || !activeFilter.custom), // set isPublic if activeFilter is either predefined or not custom
                disabled: isFilterParams,
                widgetType: "checkboxToggle",
            });
        }
        return [
            {
                view: "1column",
                collapsed: false,
                key: "static-issue",
                widgets,
            },
        ];
    }, [filters, withLocalFiltersList, activeFilter, isNewFilter, isFilterParams, isCanCreatePublicFilter]);

    function handleFilterSave() {
        const filterObject = configureFilterObject(formMainValues, attributes);
        const data = {
            query: "",
            filterObject,
            name: formHeaderValues.filterName,
            isPublic: Boolean(formHeaderValues.isPublic).toString(),
        };

        if (isEditFilter) {
            data.filterSetId = activeFilter.filterId;
        }

        const onSuccess = () => {
            setIsSaving(false);
            if (isEditFilter) {
                openFilterParams(storeKey);
            }
        };
        const onFailure = () => {
            setIsSaving(false);
        };
        setIsSaving(true);
        onFilterSave(data, onSuccess, onFailure);
    }

    const handleFilterClear = () => {
        const ignoredKeys = ["isPublic", "filterName"];

        // clear header values
        if (!isEmpty(formHeaderValues)) {
            Object.keys(formHeaderValues).forEach(key => {
                // we should not change isPublic toggle as it is not a filter
                if (ignoredKeys.includes(key)) {
                    return;
                }
                const value = formHeaderValues[key];
                if (value) {
                    const emptyValue = Array.isArray(value) ? [] : null;
                    change(FORM_HEADER_NAME(storeKey, extraFormKey), key, emptyValue);
                }
            });
        }
        // clear main values (and do not clear hidden values (from hidden widgets))
        if (!isEmpty(formMainValuesWithoutHidden)) {
            Object.keys(formMainValuesWithoutHidden).forEach(key => {
                const value = formMainValuesWithoutHidden[key];
                if (value) {
                    const emptyValue = Array.isArray(value) ? [] : null;
                    change(FORM_MAIN_NAME(storeKey, extraFormKey), key, emptyValue);
                }
            });
        }
    };

    const isLogicallyEmpty = obj => {
        return Object.values(obj).every(value => value == null || (Array.isArray(value) && value.length === 0));
    };

    async function getFiltersAttributes() {
        try {
            setIsAttributesLoading(true);
            const params = { data: { ...attributesRequestParams } };

            const { result, success } = await BaseService.get(attributesRequestKey, params);
            if (success) {
                setIsAttributesLoading(false);
                setAttributes(result, storeKey);
            }
        } catch (e) {
            setIsAttributesLoading(false);
            console.error("AttributesFiltersForm::getFiltersAttributes: Error", e);
        }
    }

    async function getFilterValues(filterId) {
        try {
            const params = { data: { id: filterId, ...attributesValuesRequestParams } };
            setIsValuesLoading(true);
            const { result, success } = await BaseService.get(attributesValuesRequestKey, params);
            if (success) {
                setAttributesValues(result);
                setIsValuesLoading(false);
                setIsFormValuesUpdateRequired(true);
            }
        } catch (e) {
            setIsValuesLoading(false);
            console.error("AttributesFiltersForm::getFilterValues: Error", e);
        }
    }

    async function getFiltersList() {
        try {
            setFiltersLoading(true, storeKey);
            const { result, success } = await BaseService.get("filters", { data: { code: filtersCode } });
            if (success) {
                // setIsLoading(false);
                setFiltersLoading(false, storeKey);
                const allFilters = result.map(filter => ({
                    ...filter,
                    filterId: filter.value,
                    filterName: filter.name,
                    custom: filter.custom,
                    predefined: filter.predefined,
                    page: 1,
                }));
                setFilters(allFilters, storeKey);
            }
        } catch (e) {
            setFiltersLoading(false, storeKey);
            console.error("AttributesFiltersForm::getFiltersList Error during filters request: ", e);
        }
    }

    function handleFilterToggle() {
        const newState = !isOpen;
        setFiltersIsOpen(newState, storeKey);
        if (onToggle && typeof onToggle === 'function') {
            onToggle(newState);
        }
    }

    useEffect(() => {
        if (attributes && attributesValues) {
            const dynFormStructure = getDynamicFormSturcture(
                attributes,
                attributesValues,
                isFilterParams,
                "1column",
                additionalWidgets
            );
            setDynParams(dynFormStructure);
        }
    }, [attributes, attributesValues, isFilterParams, useLocalFilter]);

    useEffect(() => {
        if (activeFilter) {
            setDynParams(null);
            // get values if that is not isNewFilter, othervise set values as an empty array
            if (!isNewFilter) {
                getFilterValues(activeFilterId);
            } else {
                setAttributesValues([]);
            }
        }
    }, [activeFilter, isNewFilter]);

    useEffect(() => {
        // if (dynParams) {
        //     setDynParams(null);
        // }
        if (isMounted) {
            if (useLocalFilter) {
                getFilterValues(useLocalFilter);
            } else {
                setAttributesValues([]);
            }
        }
    }, [useLocalFilter]);

    // local filter is changed => update form values
    useEffect(() => {
        if (isKeepOnUnmount && dynParams && isFormValuesUpdateRequired) {
            const initialValues = {};
            dynParams.forEach(block => {
                block.widgets.forEach(widget => {
                    initialValues[widget.key] = widget.savedValue || widget.savedValues;
                });
            });
            setDynParamsTimestamp(new Date().getTime());
            setForceMainInitialValues(initialValues);
        }
    }, [dynParams, isKeepOnUnmount, isFormValuesUpdateRequired]);

    useEffect(() => {
        // only get attributes (form fields) in case they were not initialised before
        if (!attributes) {
            getFiltersAttributes();
        }
        // get filters in case it is used without SidebarFilters (search case)
        if (!filters && withLocalFiltersList) {
            getFiltersList();
            setAttributesValues([]);
        }
        setIsMounted(true);
    }, []);

    // initHeaderForm was changed => update key to rerender the form to keep savedValue sync
    useEffect(() => {
        if (!withLocalFiltersList) {
            setHeaderFormTimestamp(new Date().getTime());
        }
    }, [initHeaderForm]);

    return (
        <div className={cx("attributes-filters-form", { "for-grid-with-filters": gridWithFilters, 'with-toggle': withToggle, 'is-filters-collapsed': withToggle && !isOpen })}>
            {!isLoading && withToggle &&  (
               <div className="attributes-filters-form__toggle">
                    {!isOpen && (
                        <div
                            className={cx("button-hover-half-circle attributes-filters-form__filter-open", {
                                "some-filter-active": formMainValues && !isLogicallyEmpty(formMainValuesWithoutHidden),
                            })}
                            onClick={handleFilterToggle}
                        >
                            <ReactSVG
                                className="attributes-filters-form__filter-icon"
                                src={`/data/svg/filters_list.svg`}
                            />
                        </div>
                    )}
                    {isOpen && (
                        <>
                            <div className="attributes-filters-form__filter-text">
                                <ReactSVG
                                    className="attributes-filters-form__filter-icon"
                                    src={`/data/svg/filters-list.svg`}
                                />
                                <span>{t("filters.filters")}</span>
                            </div>
                            <div className="button-hover-half-circle attributes-filters-form__filter-close" onClick={handleFilterToggle}>
                                <ReactSVG
                                    className="button-icon button-icon-size"
                                    src={`/data/svg/tab-right-layout-collapse-small.svg`}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
            {isFiltersLoading && <Loader loaderContainerClass="attributes-filters-form__loader" />}
            {/* <Loader loaderContainerClass="attributes-filters-form__loader" /> */}
            {withHeaderForm && (
                <div className={cx("attributes-filters-form__header", { withLocalFiltersList })}>
                    {!isFiltersLoading && (
                        <DynamicForm
                            t={t}
                            forceInitialValues={forceHeaderInitialValues}
                            formName={FORM_HEADER_NAME(storeKey, extraFormKey)}
                            dynData={initHeaderForm}
                            destroyOnUnmount={!isKeepOnUnmount}
                            portalPlacement={"left"}
                            withoutLiveSaver
                            key={`${formRerenderKey}_${dynParamsTimestamp}_${headerFormTimestamp}`}
                        />
                    )}
                    {!withLocalFiltersList && (
                        <span
                            className="attributes-filters-form__header-close"
                            onClick={() => closeFilterForm(storeKey)}
                        >
                            <i className="icon icon-close" />
                        </span>
                    )}
                </div>
            )}
            <div className="attributes-filters-form__divider"></div>
            {!isLoading && dynParams && !withoutClearButton && (
                <div className="attributes-filters-form__clear">
                    <div
                        className={cx("attributes-filters-form__clear-button", {
                            disabled: (formMainValuesWithoutHidden && isLogicallyEmpty(formMainValuesWithoutHidden)) || isFilterParams || isLoading,
                        })}
                        onClick={handleFilterClear}
                    >
                        <ReactSVG className="attributes-filters-form__clear-icon" src={`/data/svg/filters_clear.svg`} />
                        <div className="attributes-filters-form__clear-text">{t("clearFilters")}</div>
                    </div>
                </div>
            )}
            <div
                className={cx("attributes-filters-form__fields", {
                    "without-bottom-border": withoutSave || isFilterParams,
                    "without-clear-button": withoutClearButton,
                })}
            >
                {isLoading && <Loader loaderContainerClass="attributes-filters-form__loader" />}

                {!isLoading && dynParams && (
                    <DynamicForm
                        t={t}
                        forceInitialValues={forceMainInitialValues}
                        formName={FORM_MAIN_NAME(storeKey, extraFormKey)}
                        dynData={dynParams}
                        destroyOnUnmount={!isKeepOnUnmount}
                        portalPlacement={"left"}
                        withoutLiveSaver
                        key={`${formRerenderKey}_${dynParamsTimestamp}`}
                    />
                )}
            </div>
            {!withoutSave && !isFilterParams && (
                <div className="attributes-filters-form__footer">
                    <button
                        className={cx("attributes-filters-form__button btn btn-primary", {
                            disabled: isLoading || !isNoErrors || !isFormDirty,
                        })}
                        onClick={handleFilterSave}
                    >
                        <ReactSVG src={`/data/svg/input_save.svg`} />
                        <span>{t("filters.save")}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SidebarFiltersForm));
